import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigation } from "@/context/NavigationContext";
import { Plane, Loader2 } from "lucide-react";
import AppConst from "@/AppConst";
import { message } from "antd";
import { AppService } from "../services/appService";

const nameRegex = /^[A-Za-zĂÂÎȘȚăâîșț]+$/;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const passwordRules = {
  minLength: (v: string) => v.length >= 8,
  hasNumber: (v: string) => /\d/.test(v),
  hasUpper: (v: string) => /[A-Z]/.test(v),
  hasLower: (v: string) => /[a-z]/.test(v),
  hasSpecial: (v: string) => /[!@#$%^&*(),.?":{}|<>]/.test(v),
};

const countries = AppConst.countries;

const Register = () => {
  const { register, currentLanguageDictionary, registerDraft, setRegisterDraft, clearRegisterDraft } = useAuth();
  const { navigateTo } = useNavigation();

  const [lastName, setLastName] = useState<string>(registerDraft.lastName);
  const [firstName, setFirstName] = useState<string>(registerDraft.firstName);
  const [userName, setUserName] = useState<string>(registerDraft.userName);
  const [email, setEmail] = useState<string>(registerDraft.email);
  const [phone, setPhone] = useState<string>(registerDraft.phone);
  const [countryPrefix, setCountryPrefix] = useState<string>(
    registerDraft.countryPrefix || countries.find((c) => c.code === "RO")?.prefix || "+40"
  );
  const [password, setPassword] = useState<string>(registerDraft.password);
  const [repeatPassword, setRepeatPassword] = useState<string>(registerDraft.repeatPassword);
  const [seller, setSeller] = useState<boolean>(registerDraft.seller);
  const [agreedToTerms, setAgreedToTerms] = useState<boolean>(registerDraft.agreedToTerms);

  const [usernameExists, setUsernameExists] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [phoneExists, setPhoneExists] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Sync back to AuthContext memory state
  useEffect(() => {
    setRegisterDraft({
      lastName,
      firstName,
      userName,
      email,
      phone,
      countryPrefix,
      password,
      repeatPassword,
      seller,
      agreedToTerms,
    });
  }, [lastName, firstName, userName, email, phone, countryPrefix, password, repeatPassword, seller, agreedToTerms, setRegisterDraft]);

  const getTranslation = (key: string): string => {
    if (!currentLanguageDictionary) return "";
    const item = currentLanguageDictionary.find((d: any) => d.dictKey === key);
    return item?.dictValue || "";
  };

  const titleText = getTranslation("register.title");
  const surnameLabel = getTranslation("register.surname.txb");
  const surnamePlaceholder = getTranslation("register.surname.placeholder");
  const nameLabel = getTranslation("register.name.txb");
  const namePlaceholder = getTranslation("register.name.placeholder");
  const usernameLabel = getTranslation("register.username.txb");
  const usernamePlaceholder = getTranslation("register.username.placeholder");
  const phoneLabel = getTranslation("register.phone.txb");
  const emailLabel = getTranslation("register.email.txb");
  const emailPlaceholder = getTranslation("register.email.placeholder");
  const passwordLabel = getTranslation("register.password.txb");
  const repeatPasswordLabel = getTranslation("register.password.repeat.txb");
  const sellerCheckboxLabel = getTranslation("register.seller.chk");
  const accordText = getTranslation("register.accord");
  const buttonTermsText = getTranslation("register.button.terms");
  const buttonRegisterText = getTranslation("register.button.register");
  const alreadyHaveAccountText = getTranslation("register.login");
  const buttonLoginText = getTranslation("register.button.login");
  const creatingAccountText = getTranslation("register.loading.creating");
  const errorFormMessage = getTranslation("register.error.validation");
  const successTitleText = getTranslation("register.success.title");
  const successDescText = getTranslation("register.success.description");
  const backToLoginText = getTranslation("register.success.back");
  const errorMinChars = getTranslation("register.error.username.min");
  const errorUsernameExists = getTranslation("register.error.username.exists");
  const errorPhoneExists = getTranslation("register.error.phone.exists");
  const errorEmailExists = getTranslation("register.error.email.exists");
  const passwordsMatchText = getTranslation("register.error.password.match");
  const passwordsMismatchText = getTranslation("register.error.password.mismatch");

  const passRuleMin = getTranslation("register.password.rule.min");
  const passRuleUpper = getTranslation("register.password.rule.upper");
  const passRuleLower = getTranslation("register.password.rule.lower");
  const passRuleNumber = getTranslation("register.password.rule.number");
  const passRuleSpecial = getTranslation("register.password.rule.special");

  useEffect(() => {
    if (userName.length < 10) {
      setUsernameExists(false);
      return;
    }
    const timeout = setTimeout(async () => {
      const exists = await AppService.fieldExists("userName", userName);
      setUsernameExists(exists);
    }, 400);
    return () => clearTimeout(timeout);
  }, [userName]);

  useEffect(() => {
    if (email.length === 0) {
      setEmailExists(false);
      return;
    }
    const timeout = setTimeout(async () => {
      const exists = await AppService.fieldExists("email", email);
      setEmailExists(exists);
    }, 400);
    return () => clearTimeout(timeout);
  }, [email]);

  useEffect(() => {
    if (phone.replace(/\s/g, "").length === 0) {
      setPhoneExists(false);
      return;
    }
    const timeout = setTimeout(async () => {
      const exists = await AppService.fieldExists("phone", `${countryPrefix} ${phone}`);
      setPhoneExists(exists);
    }, 400);
    return () => clearTimeout(timeout);
  }, [phone, countryPrefix]);

  const firstNameValid = nameRegex.test(firstName);
  const lastNameValid = nameRegex.test(lastName);
  const usernameValid = userName.length >= 10 && !usernameExists;
  const emailValid = emailRegex.test(email) && !emailExists;

  const selectedCountry = countries.find((c) => c.prefix === countryPrefix) || countries[0];

  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, "");
    const limited = digits.slice(0, 9);
    const parts = [];
    if (limited.length > 0) parts.push(limited.slice(0, 3));
    if (limited.length >= 4) parts.push(limited.slice(3, 6));
    if (limited.length >= 7) parts.push(limited.slice(6, 9));
    return parts.join(" ");
  };

  const phoneValid = selectedCountry.regex.test(phone.replace(/\s/g, "")) && !phoneExists;

  const passwordChecks = {
    minLength: passwordRules.minLength(password),
    hasNumber: passwordRules.hasNumber(password),
    hasUpper: passwordRules.hasUpper(password),
    hasLower: passwordRules.hasLower(password),
    hasSpecial: passwordRules.hasSpecial(password),
  };
  const passwordValid = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = password === repeatPassword;
  const showMatch = repeatPassword.length > 0;

  const allValid =
    firstNameValid &&
    lastNameValid &&
    usernameValid &&
    emailValid &&
    phoneValid &&
    passwordValid &&
    passwordsMatch &&
    agreedToTerms;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    if (!allValid) {
      message.error({ content: errorFormMessage, duration: AppConst.msgDuration });
      setIsLoading(false);
      return;
    }
    try {
      const success = await register(
        firstName,
        lastName,
        userName,
        email,
        `${countryPrefix} ${phone}`,
        password,
        seller,
        agreedToTerms
      );
      if (success) {
        clearRegisterDraft();
        setRegistrationSuccess(true);
      }
    } catch (error) {
      console.error("Eroare la inregistrare", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (registrationSuccess) {
    return (
      <div className="min-h-[85vh] flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px]">
        <div className="relative w-full max-w-md overflow-hidden rounded-[2.5rem] bg-[#0F172A] border border-orange-500/30 text-center p-8">
          <div className="h-2 absolute top-0 left-0 right-0 bg-gradient-to-r from-orange-600 via-yellow-500 to-orange-600" />
          <div className="flex h-14 w-14 mx-auto items-center justify-center rounded-2xl bg-orange-500/10 text-orange-500 border border-orange-500/20 mb-4">
            <Plane size={30} className="rotate-45" />
          </div>
          <h1 className="text-xl font-black text-white italic leading-tight mb-2">{successTitleText}</h1>
          <p className="text-slate-400 text-xs leading-relaxed px-2">{successDescText}</p>
          <button
            onClick={() => navigateTo("login")}
            className="w-full mt-6 px-5 py-3 rounded-xl bg-orange-600 text-white text-xs font-black uppercase tracking-widest hover:bg-orange-500 transition-all block text-center"
          >
            {backToLoginText}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[85vh] flex items-center justify-center p-4 relative bg-black/40 backdrop-blur-[2px]">
      <div className="relative w-full max-w-xl overflow-hidden rounded-[2rem] bg-[#0F172A] border border-orange-500/30 shadow-[0_0_40px_rgba(249,115,22,0.1)]">
        <div className="h-1.5 bg-gradient-to-r from-orange-600 via-yellow-500 to-orange-600" />

        <div className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-orange-500/10 text-orange-500 border border-orange-500/20">
              <Plane size={24} className="rotate-45" />
            </div>
            <div>
              <p className="text-[9px] font-black tracking-[0.25em] text-orange-500 uppercase">HangarX Crew</p>
              <h3 className="text-xl font-black text-white italic leading-tight">{titleText}</h3>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3.5">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block pl-1">{surnameLabel}</label>
                <input
                  type="text"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={`w-full h-9 px-3 rounded-lg bg-slate-950/60 border text-white text-xs focus:outline-none focus:ring-1 transition-all placeholder:text-slate-800 ${
                    lastName.length > 0 && !lastNameValid ? "border-red-500 focus:ring-red-500/30" : "border-orange-500/10 focus:border-orange-500/40"
                  }`}
                  placeholder={surnamePlaceholder}
                />
              </div>
              <div className="space-y-1">
                <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block pl-1">{nameLabel}</label>
                <input
                  type="text"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={`w-full h-9 px-3 rounded-lg bg-slate-950/60 border text-white text-xs focus:outline-none focus:ring-1 transition-all placeholder:text-slate-800 ${
                    firstName.length > 0 && !firstNameValid ? "border-red-500 focus:ring-red-500/30" : "border-orange-500/10 focus:border-orange-500/40"
                  }`}
                  placeholder={namePlaceholder}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1 relative">
                <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block pl-1">{usernameLabel}</label>
                <input
                  type="text"
                  required
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  className={`w-full h-9 px-3 rounded-lg bg-slate-950/60 border text-white text-xs focus:outline-none focus:ring-1 transition-all placeholder:text-slate-800 ${
                    (userName.length > 0 && userName.length < 10) || usernameExists ? "border-red-500 focus:ring-red-500/30" : "border-orange-500/10 focus:border-orange-500/40"
                  }`}
                  placeholder={usernamePlaceholder}
                />
                {userName.length > 0 && userName.length < 10 && <p className="absolute text-[8px] text-red-400 right-1 top-0">{errorMinChars}</p>}
                {usernameExists && userName.length >= 10 && <p className="absolute text-[8px] text-red-400 right-1 top-0">{errorUsernameExists}</p>}
              </div>

              <div className="space-y-1 relative">
                <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block pl-1">{phoneLabel}</label>
                <div className="flex rounded-lg overflow-hidden bg-slate-950/60 border border-orange-500/10 focus-within:border-orange-500/40 focus-within:ring-1 transition-all">
                  <select
                    value={countryPrefix}
                    onChange={(e) => { setCountryPrefix(e.target.value); setPhone(""); }}
                    className="h-9 px-2 bg-transparent border-none text-[11px] text-white focus:outline-none cursor-pointer appearance-none border-r border-orange-500/10"
                  >
                    {countries.map((c) => (
                      <option key={c.code} value={c.prefix} className="bg-[#0F172A]">
                        {c.flag} {c.prefix}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    required
                    value={formatPhone(phone)}
                    onChange={(e) => setPhone(e.target.value)}
                    className={`flex-1 bg-transparent h-9 px-2 text-white text-xs focus:outline-none ${phone.length > 0 && !phoneValid ? "text-red-400" : ""}`}
                    placeholder={selectedCountry.placeholder}
                  />
                </div>
                {phoneExists && phone.replace(/\s/g, "").length > 0 && <p className="absolute text-[8px] text-red-400 right-1 top-0">{errorPhoneExists}</p>}
              </div>
            </div>

            <div className="space-y-1 relative">
              <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block pl-1">{emailLabel}</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full h-9 px-3 rounded-lg bg-slate-950/60 border text-white text-xs focus:outline-none focus:ring-1 transition-all placeholder:text-slate-800 ${
                  email.length > 0 && (!emailRegex.test(email) || emailExists) ? "border-red-500 focus:ring-red-500/30" : "border-orange-500/10 focus:border-orange-500/40"
                }`}
                placeholder={emailPlaceholder}
              />
              {emailExists && email.length > 0 && <p className="absolute text-[8px] text-red-400 right-1 top-0">{errorEmailExists}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block pl-1">{passwordLabel}</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-9 px-3 rounded-lg bg-slate-950/60 border border-orange-500/10 text-white text-xs focus:outline-none focus:border-orange-500/40"
                  placeholder="••••••••"
                />
              </div>
              <div className="space-y-1 relative">
                <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block pl-1">{repeatPasswordLabel}</label>
                <input
                  type="password"
                  required
                  value={repeatPassword}
                  onChange={(e) => setRepeatPassword(e.target.value)}
                  className={`w-full h-9 px-3 rounded-lg bg-slate-950/60 border text-white text-xs focus:outline-none focus:ring-1 transition-all placeholder:text-slate-800 ${
                    showMatch ? (passwordsMatch ? "border-green-500 focus:ring-green-500/20" : "border-red-500 focus:ring-red-500/20") : "border-orange-500/10 focus:border-orange-500/40"
                  }`}
                  placeholder="••••••••"
                />
                {showMatch && (
                  <p className={`absolute text-[8px] font-semibold right-2 bottom-2.5 ${passwordsMatch ? "text-green-400" : "text-red-400"}`}>
                    {passwordsMatch ? passwordsMatchText : passwordsMismatchText}
                  </p>
                )}
              </div>
            </div>

            {password.length > 0 && !passwordValid && (
              <div className="flex flex-wrap gap-x-3 gap-y-1 p-2 rounded-lg bg-orange-500/5 border border-orange-500/5 text-[8px] font-medium justify-center">
                <span className={passwordChecks.minLength ? "text-green-400" : "text-slate-600"}>✓ {passRuleMin}</span>
                <span className={passwordChecks.hasUpper ? "text-green-400" : "text-slate-600"}>✓ {passRuleUpper}</span>
                <span className={passwordChecks.hasLower ? "text-green-400" : "text-slate-600"}>✓ {passRuleLower}</span>
                <span className={passwordChecks.hasNumber ? "text-green-400" : "text-slate-600"}>✓ {passRuleNumber}</span>
                <span className={passwordChecks.hasSpecial ? "text-green-400" : "text-slate-600"}>✓ {passRuleSpecial}</span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 pl-0.5">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="wantToSell"
                  checked={seller}
                  onChange={(e) => setSeller(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-orange-500/20 bg-slate-950 text-orange-600 focus:ring-0 cursor-pointer accent-orange-600"
                />
                <label htmlFor="wantToSell" className="text-[11px] text-slate-300 font-medium cursor-pointer select-none">{sellerCheckboxLabel}</label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="terms"
                  required
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-orange-500/20 bg-slate-950 text-orange-600 focus:ring-0 cursor-pointer accent-orange-600"
                />
                <label htmlFor="terms" className="text-[11px] text-slate-400 select-none cursor-pointer truncate">
                  {accordText}{" "}
                  <button
                    type="button"
                    onClick={() => navigateTo("terms-conditions", { fromRegister: true })}
                    className="text-orange-500 hover:text-orange-400 font-bold inline hover:underline transition-colors"
                  >
                    {buttonTermsText}
                  </button>
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={!allValid || isLoading}
              className="w-full h-11 rounded-xl bg-orange-600 text-white text-xs font-black uppercase tracking-widest hover:bg-orange-500 disabled:opacity-40 transition-all shadow-[0_0_15px_rgba(249,115,22,0.2)] flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {creatingAccountText}
                </>
              ) : (
                buttonRegisterText
              )}
            </button>
          </form>

          <div className="pt-3 border-t border-slate-500/10 text-center text-[11px] text-slate-500 font-semibold">
            {alreadyHaveAccountText}{" "}
            <button
              type="button"
              onClick={() => {
                clearRegisterDraft();
                navigateTo("login");
              }}
              className="text-white hover:underline font-bold ml-1"
            >
              {buttonLoginText}
            </button>
          </div>
        </div>

        <div className="absolute -bottom-12 -right-12 opacity-[0.015] pointer-events-none text-white">
          <Plane size={180} className="rotate-45" />
        </div>
      </div>
    </div>
  );
};

export default Register;